<template>
  <div class="flexing flex-col w-full py-6 gap-6" :class="{ 'flex-grow px-6': isEmailChange }">
    <div
      class="flex flex-row gap-4 text-texts-standard-default text-28 relative -bottom-2"
      :class="{ 'text-32': isEmailChange }"
    >
      <span>{{ $t('settings.verificationEmailSent') }}</span>
      <app-icon
        v-tippy="{
          content: $replacePlaceholder(
            $t('settings.verificationEmailNotReceived'),
            '{email}',
            supportMail,
          ),
          theme: 'WSM',
          placement: 'top',
          maxWidth: '35rem',
        }"
        icon-name="info-44"
      />
    </div>
    <div class="relative flexing flex-row gap-4">
      <span class="text-texts-standard-important font-bold text-36 relative -top-2">
        {{ email }}
      </span>
    </div>
    <app-button
      type="submit"
      btn-type="primary"
      :disabled="isLoading"
      @click="$emit('change-email')"
    >
      {{ $t('settings.changeEmail') }}
      <component-loading class="absolute inset-0" :is-loading="isLoading" />
    </app-button>
    <template v-if="!isEmailChange">
      <div class="use-text flexing text-texts-standard-default uppercase m-auto text-28">
        <div class="use-text__divider use-text__divider--left" />
        <span v-if="!isConnection || !allSocialConnected" class="use-text__label">
          {{ $t('homepage.orUse') }}
        </span>
        <div class="use-text__divider use-text__divider--right" />
      </div>
      <div class="flex flex-col w-full space-y-6">
        <facebook-button
          :is-disabled="isLoading"
          :monochrome="!isConnection || fbNotConnected"
          :status="connectedAccounts?.facebook"
          :context="context"
          @facebook-connection="onFacebookConnection"
        />
        <google-button
          :is-disabled="isLoading"
          :status="connectedAccounts?.google"
          :context="context"
          @google-connection="onGoogleConnection"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import ComponentLoading from '../GlobalComponents/ComponentLoading.vue'
import FacebookButton from './FacebookButton.vue'
import GoogleButton from './GoogleButton.vue'
import { supportMail } from '@/globalVariables'
import { defineComponent, type PropType } from 'vue'
import { ConnectedAccountStatus, LoginFormContext } from '@/enums/LoginForm'
import { mapActions, mapState } from 'pinia'
import { useAccountStore } from '@/store/pinia/accountStore'

export interface ComponentData {
  supportMail: typeof supportMail
  LoginFormContext: typeof LoginFormContext
  ConnectedAccountStatus: typeof ConnectedAccountStatus
}

export default defineComponent({
  components: {
    ComponentLoading,
    FacebookButton,
    GoogleButton,
  },
  props: {
    email: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    context: {
      type: String as PropType<LoginFormContext>,
      default: '',
      validator: (value: LoginFormContext) => Object.values(LoginFormContext).includes(value),
    },
    isEmailChange: {
      type: Boolean,
      default: false,
    },
    isConnection: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change-email'],
  data(): ComponentData {
    return {
      supportMail,
      LoginFormContext,
      ConnectedAccountStatus,
    }
  },
  computed: {
    ...mapState(useAccountStore, ['connectedAccounts']),
    allSocialConnected(): boolean {
      return (
        this.connectedAccounts?.facebook !== ConnectedAccountStatus.NotConnected &&
        this.connectedAccounts?.google !== ConnectedAccountStatus.NotConnected
      )
    },
    fbNotConnected(): boolean {
      return this.connectedAccounts?.facebook === ConnectedAccountStatus.NotConnected
    },
  },
  methods: {
    ...mapActions(useAccountStore, ['loadConnectedAccounts']),
    async onFacebookConnection(): Promise<void> {
      await this.loadConnectedAccounts(true)
    },
    async onGoogleConnection(): Promise<void> {
      await this.loadConnectedAccounts(true)
    },
  },
})
</script>
